import * as THREE from 'three';
import { params } from '../config.js';
import * as SceneModule from '../scene.js';
import { airplaneState } from '../airplane/state.js';
import { getAirplaneGroup } from '../airplane/create.js';
import { cameraOrbit } from '../controls/mouse.js';
import { pane } from '../ui/tweakpane.js';

// Reusable vector for camera position calculations (avoid GC pressure)
const _targetCamPos = new THREE.Vector3();

// Landed camera state
const landedCamera = {
  distanceOffset: 0,      // extra distance when landed (lerps toward target)
  targetDistOffset: 2.5,  // how much to zoom out when landed
  orbitSpeed: 0.15,       // radians per second auto-orbit
  elevationTarget: 0.15,  // slight upward angle when landed
};

export function cycleCameraMode() {
  const modes = ['chase', 'topdown'];
  const currentIndex = modes.indexOf(params.cameraMode);
  const nextIndex = (currentIndex + 1) % modes.length;
  setCameraMode(modes[nextIndex]);
}

export function setCameraMode(mode) {
  params.cameraMode = mode;
  
  // All modes now follow the airplane, so always enable it
  const airplaneGroup = getAirplaneGroup();
  if (!airplaneState.enabled) {
    params.airplaneEnabled = true;
    airplaneState.enabled = true;
    if (airplaneGroup) airplaneGroup.visible = true;
  }
  // Reset airplane state when switching modes
  airplaneState.heading = 0;
  airplaneState.targetHeading = 0;
  airplaneState.y = 0;
  airplaneState.targetY = 0;
  airplaneState.tilt = 0;
  airplaneState.pitch = 0;
  airplaneState.worldPos.set(0, airplaneState.baseAltitude, 0);
  // Reset camera orbit when switching modes
  cameraOrbit.azimuth = 0;
  cameraOrbit.elevation = 0;
  
  // Reset flight state and hide overlays
  airplaneState.flightState = 'flying';
  airplaneState.collisionShake = 0;
  const crashOverlay = document.getElementById('crash-overlay');
  if (crashOverlay) crashOverlay.classList.remove('visible');
  if (pane) pane.refresh();
}

export function updateCameraMode(delta) {
  const { camera } = SceneModule;
  const airplaneGroup = getAirplaneGroup();
  if (!airplaneGroup || !airplaneState.enabled) return;
  
  const isLanded = airplaneState.flightState === 'landed';
  const isCharging = airplaneState.launchCharging;

  if (isLanded && !isCharging) {
    // Landed: gentle auto-orbit and zoom out
    cameraOrbit.azimuth += landedCamera.orbitSpeed * delta;
    // Ease elevation toward a slight upward angle
    cameraOrbit.elevation += (landedCamera.elevationTarget - cameraOrbit.elevation) * Math.min(1, 2 * delta);
    // Ease distance offset toward target (zoom out)
    landedCamera.distanceOffset += (landedCamera.targetDistOffset - landedCamera.distanceOffset) * Math.min(1, 2 * delta);
  } else {
    // Flying or charging: ease orbit, elevation, and distance back to defaults
    const easeSpeed = cameraOrbit.easeSpeed * delta;
    
    // Normalize azimuth to shortest path (wrap to [-π, π])
    while (cameraOrbit.azimuth > Math.PI) cameraOrbit.azimuth -= 2 * Math.PI;
    while (cameraOrbit.azimuth < -Math.PI) cameraOrbit.azimuth += 2 * Math.PI;
    
    if (Math.abs(cameraOrbit.azimuth) > 0.001) {
      cameraOrbit.azimuth -= cameraOrbit.azimuth * Math.min(1, easeSpeed);
    } else {
      cameraOrbit.azimuth = 0;
    }
    if (Math.abs(cameraOrbit.elevation) > 0.001) {
      cameraOrbit.elevation -= cameraOrbit.elevation * Math.min(1, easeSpeed);
    } else {
      cameraOrbit.elevation = 0;
    }
    // Ease distance offset back to 0
    if (Math.abs(landedCamera.distanceOffset) > 0.01) {
      landedCamera.distanceOffset -= landedCamera.distanceOffset * Math.min(1, easeSpeed);
    } else {
      landedCamera.distanceOffset = 0;
    }
  }
  
  // Camera mode logic with orbit support
  // Use stable airplane position (worldPos) to avoid shake affecting camera
  // Camera positions behind the airplane based on heading direction
  if (params.cameraMode === 'chase') {
    // Chase cam: positioned behind the airplane based on heading
    const chaseDist = params.chaseCamDistance + landedCamera.distanceOffset;
    const chaseHeight = params.chaseCamHeight;
    
    // Use stable airplane position
    const stablePos = airplaneState.worldPos;
    
    // Calculate "behind" direction based on heading + orbit
    // heading 0 = facing -Z, so "behind" is +Z
    const behindAngle = airplaneState.heading + Math.PI + cameraOrbit.azimuth;
    const orbitX = Math.sin(behindAngle) * chaseDist;
    const orbitZ = -Math.cos(behindAngle) * chaseDist;
    // Apply elevation to height
    const elevationOffset = cameraOrbit.elevation * chaseDist;
    
    _targetCamPos.set(
      stablePos.x + orbitX,
      stablePos.y + chaseHeight + elevationOffset,
      stablePos.z + orbitZ
    );
    
    // Smooth camera movement
    camera.position.lerp(_targetCamPos, Math.min(1, 8 * delta));
    
    // Look at the stable airplane position
    camera.lookAt(stablePos);
  } else if (params.cameraMode === 'topdown') {
    // Top-down cam: above the airplane, rotates with heading
    const topHeight = params.topCamHeight;
    
    // Use stable airplane position (worldPos) instead of shake-affected position
    const stablePos = airplaneState.worldPos;
    
    // Apply orbit for slight offset from directly above
    const orbitOffset = cameraOrbit.elevation * topHeight * 0.5;
    const totalAngle = airplaneState.heading + cameraOrbit.azimuth;
    const orbitX = Math.sin(totalAngle) * orbitOffset;
    const orbitZ = -Math.cos(totalAngle) * orbitOffset;
    
    _targetCamPos.set(
      stablePos.x + orbitX,
      stablePos.y + topHeight,
      stablePos.z + orbitZ
    );
    
    // Smooth camera movement
    camera.position.lerp(_targetCamPos, Math.min(1, 8 * delta));
    
    // For top-down, set rotation directly instead of lookAt to avoid gimbal lock
    // Rotate to look straight down (-Y), with airplane heading as "up" direction
    camera.rotation.set(-Math.PI / 2, 0, -airplaneState.heading - cameraOrbit.azimuth);
  }
}
